import React from 'react';
import styles from './TelegramPostPreview.module.scss';

type Token = string | React.ReactElement;

/** List of token parsing rules. Add new rules here to extend parsing. */
const TELEGRAM_MARKDOWN_RULES: {
  re: RegExp;
  render: (match: RegExpExecArray) => React.ReactElement;
}[] = [
  {
    // bold
    re: /(?<!\S)\*\*(?=\s*\S)([^\n]*?\S\s*)\*\*(?=\s|$)/g,
    render: (match) => <strong>{match[1].trim()}</strong>,
  },
  {
    // italic
    re: /(?<!\S)__(?=\s*\S)([^\n]*?\S\s*)__(?=\s|$)/g,
    render: (match) => <em>{match[1].trim()}</em>,
  },
  {
    // plain URL
    re: /(?<=\s|^)(https?:\/\/\S+\.\S+)/g,
    render: (match) => (
      <a
        className={styles['post-link']}
        href={match[0]}
        rel="noopener noreferrer"
        target="_blank">
        {match[0]}
      </a>
    ),
  },
];

/** Parses Telegram-flavoured markdown into an array of strings and React elements. */
export const parseTelegramTokens = (text: string): Token[] => {
  const tokens: Token[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const earliestMatch = TELEGRAM_MARKDOWN_RULES.reduce<{
      index: number;
      match: RegExpExecArray;
      render: (match: RegExpExecArray) => React.ReactElement;
    } | null>((earliest, rule) => {
      rule.re.lastIndex = 0;
      const match = rule.re.exec(remaining);

      if (match && (!earliest || match.index < earliest.index)) {
        return { index: match.index, match, render: rule.render };
      }

      return earliest;
    }, null);

    if (!earliestMatch) {
      tokens.push(remaining);
      break;
    }

    if (earliestMatch.index > 0) {
      tokens.push(remaining.slice(0, earliestMatch.index));
    }

    tokens.push(
      React.cloneElement(earliestMatch.render(earliestMatch.match), {
        key: key++,
      }),
    );
    remaining = remaining.slice(
      earliestMatch.index + earliestMatch.match[0].length,
    );
  }

  return tokens;
};
